import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PlusIcon, PencilIcon, TrashIcon, ExternalLinkIcon, SearchIcon, EyeIcon } from 'lucide-react'
import { apiClient, TenantResponse } from '../lib/api'
import { useNavigate } from 'react-router-dom'
import EditTenantModal from '../components/EditTenantModal'
import ConfirmationModal from '../components/ConfirmationModal'
import Toast from '../components/Toast'
import { getTenantUrl, getTenantDisplayUrl } from '../lib/utils'
import { getTenantStatusBadgeClass, getTenantStatusLabel, isTenantOperational } from '../lib/tenantStatus'

export default function Tenants() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'product' | 'service'>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'building' | 'inactive' | 'operational'>('all')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'alpha'>('newest')
  const [deleteError, setDeleteError] = useState('')
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<TenantResponse | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: tenants = [], isLoading, error: fetchError } = useQuery({
    queryKey: ['tenants'],
    queryFn: apiClient.getTenants,
    retry: 3,
    retryDelay: 1000,
  })

  const deleteTenantMutation = useMutation({
    mutationFn: apiClient.deleteTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      setDeleteError('')
    },
    onError: (error: Error) => {
      setDeleteError(error.message)
    },
  })

  const processedTenants = useMemo(() => {
    let result = [...tenants]

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (t) => t.name.toLowerCase().includes(q) || t.subdomain.toLowerCase().includes(q)
      )
    }

    if (filterType !== 'all') {
      result = result.filter((t) => t.tenant_type === filterType)
    }

    if (filterStatus === 'operational') {
      result = result.filter((t) => isTenantOperational(t.status))
    } else if (filterStatus !== 'all') {
      result = result.filter((t) => t.status === filterStatus)
    }

    if (sortBy === 'newest') {
      result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    } else if (sortBy === 'oldest') {
      result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    } else if (sortBy === 'alpha') {
      result.sort((a, b) => a.name.localeCompare(b.name))
    }

    return result
  }, [tenants, searchQuery, filterType, filterStatus, sortBy])

  const handleView = (tenant: TenantResponse) => navigate(`/tenants/${tenant.id}`)
  const handleEdit = (tenant: TenantResponse) => { setSelectedTenant(tenant); setEditModalOpen(true) }
  const handleDelete = (tenant: TenantResponse) => { setSelectedTenant(tenant); setDeleteModalOpen(true) }

  const confirmDelete = () => {
    if (selectedTenant) {
      deleteTenantMutation.mutate(selectedTenant.id)
      setDeleteModalOpen(false)
      setSelectedTenant(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading tenants...</div>
      </div>
    )
  }

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Tenant</h1>
          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-sm font-semibold text-gray-600 bg-gray-100 rounded-full">
            {tenants.length}
          </span>
        </div>
        <button
          onClick={() => navigate('/tenants/create')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          New Tenant
        </button>
      </div>

      <p className="text-sm text-gray-500 -mt-4 mb-6">
        Manage your platform tenants and their configurations
      </p>

      {/* ── Search + Filters ── */}
      <div className="flex items-center gap-3 mb-6 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
        <div className="flex-1 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by Tenant name"
            className="w-full pl-9 pr-3 py-2 text-sm border-0 focus:outline-none bg-transparent placeholder-gray-400"
          />
        </div>
        <div className="h-5 w-px bg-gray-200" />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          className="text-sm text-gray-600 border-0 focus:outline-none bg-transparent cursor-pointer pr-6"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="alpha">A → Z</option>
        </select>
        <div className="h-5 w-px bg-gray-200" />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as typeof filterType)}
          className="text-sm text-gray-600 border-0 focus:outline-none bg-transparent cursor-pointer pr-6"
        >
          <option value="all">All Types</option>
          <option value="product">Product</option>
          <option value="service">Service</option>
        </select>
        <div className="h-5 w-px bg-gray-200" />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
          className="text-sm text-gray-600 border-0 focus:outline-none bg-transparent cursor-pointer pr-6"
        >
          <option value="all">All status</option>
          <option value="operational">Operational</option>
          <option value="active">Active</option>
          <option value="building">Building</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* ── Error ── */}
      {(fetchError || deleteError) && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {deleteError || (fetchError as Error)?.message || 'An error occurred'}
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Subdomain</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Type</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Products</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Created at</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {processedTenants.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-400">
                  {searchQuery
                    ? `No tenants matching "${searchQuery}"`
                    : tenants.length === 0
                      ? 'No tenants found. Create your first tenant to get started.'
                      : 'No tenants match the selected filters.'}
                </td>
              </tr>
            ) : (
              processedTenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-50/60 transition-colors">
                  {/* Name */}
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {tenant.name}
                  </td>

                  {/* Subdomain */}
                  <td className="px-6 py-4 text-sm">
                    <a
                      href={getTenantUrl(tenant.subdomain)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 transition-colors"
                    >
                      {getTenantDisplayUrl(tenant.subdomain)}
                      <ExternalLinkIcon className="h-3 w-3" />
                    </a>
                  </td>

                  {/* Type */}
                  <td className="px-6 py-4">
                    <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                      {tenant.tenant_type === 'product' ? 'Product' : 'Service'}
                    </span>
                  </td>

                  {/* Products */}
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {(tenant.client_admin_enabled ?? true) && (
                        <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                          Client Admin
                        </span>
                      )}
                      {tenant.domain_expert_enabled && (
                        <span className="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                          Expert Agents
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getTenantStatusBadgeClass(tenant.status)}`}
                    >
                      {getTenantStatusLabel(tenant.status)}
                    </span>
                  </td>

                  {/* Created at */}
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(tenant.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleView(tenant)}
                        title="View"
                        className="text-gray-400 hover:text-primary-600 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(tenant)}
                        title="Edit"
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(tenant)}
                        disabled={deleteTenantMutation.isLoading}
                        title="Delete"
                        className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Modals ── */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <EditTenantModal
        isOpen={editModalOpen}
        onClose={() => { setEditModalOpen(false); setSelectedTenant(null) }}
        tenant={selectedTenant}
        onSuccess={() => setToast({ message: 'Tenant updated successfully', type: 'success' })}
        onError={(msg) => setToast({ message: msg, type: 'error' })}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setSelectedTenant(null) }}
        onConfirm={confirmDelete}
        title="Delete Tenant"
        message={`Are you sure you want to delete "${selectedTenant?.name}"? This action cannot be undone and will remove all associated data.`}
        confirmText="Delete Tenant"
        type="danger"
        isLoading={deleteTenantMutation.isLoading}
      />
    </div>
  )
}
